/*
 * Archive member profile photo - the portrait in the Archive Persona header (/pages/archive-persona).
 *
 * The photo belongs to the member's Archive Profile and is only ever an image the member chose
 * here. It is processed in the browser (centre-cropped square, resized, re-encoded - which also
 * drops EXIF/GPS metadata) and then uploaded through the Archive API, which validates the stored
 * bytes again before it is published.
 *
 * archive-persona.js connects this module to its toast and API base and reports the member's
 * current photo after the profile loads. Guests only ever see the default portrait.
 */
(function () {
  'use strict';

  // Mirrors ArchiveProfileImageRules in ArchiveProfileService.Api/Models/Profiles/
  // ArchiveProfileImage.cs. The popover copy below is generated from these values, and a theme
  // test compares them with the C# constants so the two cannot silently drift apart.
  const ARCHIVE_PROFILE_PHOTO_RULES = Object.freeze({
    acceptedTypes: Object.freeze(['image/jpeg', 'image/png', 'image/webp']),
    maxBytes: 2 * 1024 * 1024,
    minDimension: 256,
    maxDimension: 1024,
    targetDimension: 512,
    webpQuality: 0.86,
    jpegQuality: 0.88
  });

  const TYPE_LABELS = { 'image/jpeg': 'JPEG', 'image/png': 'PNG', 'image/webp': 'WebP' };

  const ERROR_MESSAGES = {
    unsupported_format: 'Unsupported image format. Please choose a JPEG, PNG or WebP image.',
    too_large: 'Image is too large. Please choose a smaller image.',
    too_small: `Image is too small. Please choose an image at least ${ARCHIVE_PROFILE_PHOTO_RULES.minDimension} × ${ARCHIVE_PROFILE_PHOTO_RULES.minDimension} px.`,
    could_not_process: 'Image could not be processed. Please try another image.',
    default: 'Your photo could not be saved. Please try again.'
  };

  function formatAcceptedTypes(rules = ARCHIVE_PROFILE_PHOTO_RULES) {
    const labels = rules.acceptedTypes.map(type => TYPE_LABELS[type] || type);
    return labels.length > 1
      ? `${labels.slice(0, -1).join(', ')} or ${labels[labels.length - 1]}`
      : labels[0];
  }

  function formatMegabytes(bytes) {
    const mb = bytes / (1024 * 1024);
    return `${Number.isInteger(mb) ? mb : mb.toFixed(1)} MB`;
  }

  // The customer-facing requirement list, derived from the same rules the upload flow enforces.
  function requirementItems(rules = ARCHIVE_PROFILE_PHOTO_RULES) {
    return [
      formatAcceptedTypes(rules),
      `Maximum ${formatMegabytes(rules.maxBytes)} after processing`,
      'Square image recommended',
      `Minimum ${rules.minDimension} × ${rules.minDimension} px`,
      'Your photo will be cropped and optimised for the Archive'
    ];
  }

  function computeCenterCrop(width, height) {
    const size = Math.min(width, height);
    return {
      sx: Math.floor((width - size) / 2),
      sy: Math.floor((height - size) / 2),
      size
    };
  }

  function photoError(code) {
    const error = new Error(ERROR_MESSAGES[code] || ERROR_MESSAGES.default);
    error.code = code;
    return error;
  }

  async function decodeImage(file) {
    if (typeof createImageBitmap === 'function') {
      try {
        return await createImageBitmap(file, { imageOrientation: 'from-image' });
      } catch (error) {
        // Fall through to <img> decoding (older Safari rejects the options object).
      }
    }

    const url = URL.createObjectURL(file);
    try {
      const image = new Image();
      image.decoding = 'async';
      image.src = url;
      await image.decode();
      return image;
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  function canvasToBlob(canvas, type, quality) {
    return new Promise(resolve => canvas.toBlob(resolve, type, quality));
  }

  // Centre-crop to a square, resize to targetDimension, re-encode as WebP (JPEG fallback).
  async function processImageFile(file, rules = ARCHIVE_PROFILE_PHOTO_RULES) {
    if (!file || !rules.acceptedTypes.includes(file.type)) {
      throw photoError('unsupported_format');
    }

    let source;
    try {
      source = await decodeImage(file);
    } catch (error) {
      throw photoError('could_not_process');
    }

    const width = source.naturalWidth || source.width;
    const height = source.naturalHeight || source.height;

    if (!width || !height) {
      throw photoError('could_not_process');
    }

    if (Math.min(width, height) < rules.minDimension) {
      throw photoError('too_small');
    }

    const crop = computeCenterCrop(width, height);
    const target = rules.targetDimension;
    const canvas = document.createElement('canvas');
    canvas.width = target;
    canvas.height = target;

    const context = canvas.getContext('2d');
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';
    context.drawImage(source, crop.sx, crop.sy, crop.size, crop.size, 0, 0, target, target);
    source.close?.();

    let blob = await canvasToBlob(canvas, 'image/webp', rules.webpQuality);
    if (!blob || blob.type !== 'image/webp') {
      blob = await canvasToBlob(canvas, 'image/jpeg', rules.jpegQuality);
    }

    if (!blob || !rules.acceptedTypes.includes(blob.type)) {
      throw photoError('could_not_process');
    }

    if (blob.size > rules.maxBytes) {
      throw photoError('too_large');
    }

    return { blob, contentType: blob.type, width: target, height: target, crop };
  }

  // ------------------------------------------------------------------------------ portrait UI

  const connection = {
    apiBase: '/apps/archive',
    showToast: null
  };

  let portrait = null;

  function toast(options) {
    if (typeof connection.showToast === 'function') {
      connection.showToast(options);
    }
  }

  async function requestJson(path, options = {}) {
    const response = await fetch(`${connection.apiBase}${path}`, {
      credentials: 'same-origin',
      ...options,
      headers: { 'Content-Type': 'application/json', ...(options.headers || {}) }
    });

    let body = null;
    try {
      body = await response.json();
    } catch (error) {
      body = null;
    }

    if (!response.ok) {
      const error = new Error(body?.message || ERROR_MESSAGES.default);
      error.code = body?.error || 'request_failed';
      error.serverMessage = body?.message || null;
      throw error;
    }

    return body || {};
  }

  function errorMessage(error) {
    return error?.serverMessage || ERROR_MESSAGES[error?.code] || ERROR_MESSAGES.default;
  }

  function isSafeImageUrl(url) {
    return typeof url === 'string' && /^https:\/\//i.test(url);
  }

  function create(element) {
    const frame = element.querySelector('[data-archive-portrait-frame]');
    const image = element.querySelector('[data-archive-portrait-image]');
    const isMember = element.hasAttribute('data-archive-portrait-member');

    const state = { hasPhoto: false, busy: false, ready: false };

    const refs = isMember
      ? {
          actions: element.querySelector('[data-archive-portrait-actions]'),
          choose: element.querySelector('[data-archive-portrait-choose]'),
          separator: element.querySelector('[data-archive-portrait-separator]'),
          remove: element.querySelector('[data-archive-portrait-remove]'),
          input: element.querySelector('[data-archive-portrait-input]'),
          confirm: element.querySelector('[data-archive-portrait-confirm]'),
          confirmYes: element.querySelector('[data-archive-portrait-confirm-yes]'),
          confirmCancel: element.querySelector('[data-archive-portrait-confirm-cancel]'),
          info: element.querySelector('[data-archive-portrait-info]'),
          infoButton: element.querySelector('[data-archive-portrait-info-button]'),
          popover: element.querySelector('[data-archive-portrait-popover]'),
          popoverClose: element.querySelector('[data-archive-portrait-popover-close]'),
          status: element.querySelector('[data-archive-portrait-status]')
        }
      : null;

    function showImage(url) {
      if (isSafeImageUrl(url)) {
        image.src = url;
        image.hidden = false;
        frame.classList.add('archive-persona-portrait__frame--photo');
      } else {
        image.removeAttribute('src');
        image.hidden = true;
        frame.classList.remove('archive-persona-portrait__frame--photo');
      }
    }

    // A photo that fails to load (e.g. its signed URL expired while the page was open) falls
    // back to the default portrait; the member can still change or remove it.
    image.addEventListener('error', () => {
      image.hidden = true;
      frame.classList.remove('archive-persona-portrait__frame--photo');
    });

    function syncActions() {
      if (!refs) {
        return;
      }

      refs.actions.hidden = !state.ready;
      refs.choose.textContent = state.hasPhoto ? 'Change photo' : 'Add photo';
      refs.choose.setAttribute('aria-label', state.hasPhoto ? 'Change profile photo' : 'Add profile photo');
      const confirming = !refs.confirm.hidden;
      refs.choose.hidden = confirming;
      refs.separator.hidden = confirming || !state.hasPhoto;
      refs.remove.hidden = confirming || !state.hasPhoto;
      element.classList.toggle('archive-persona-portrait--busy', state.busy);
      frame.setAttribute('aria-busy', state.busy ? 'true' : 'false');
      refs.choose.disabled = state.busy;
      refs.remove.disabled = state.busy;
    }

    function setImage(url) {
      state.hasPhoto = isSafeImageUrl(url);
      state.ready = true;
      showImage(url);
      syncActions();
    }

    function announce(text) {
      if (refs?.status) {
        refs.status.textContent = text;
      }
    }

    // ---------------------------------------------------------------- info popover

    function positionPopover() {
      const popover = refs.popover;
      popover.style.left = '';
      popover.style.right = '0px';
      popover.classList.remove('archive-persona-portrait__popover--up');

      const gap = 12;
      const rect = popover.getBoundingClientRect();
      const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
      let shift = 0;

      if (rect.left < gap) {
        shift = gap - rect.left;
      } else if (rect.right > viewportWidth - gap) {
        shift = viewportWidth - gap - rect.right;
      }

      if (shift) {
        popover.style.right = `${-shift}px`;
      }

      const buttonRect = refs.infoButton.getBoundingClientRect();
      const spaceBelow = window.innerHeight - buttonRect.bottom;
      if (rect.height + gap > spaceBelow && buttonRect.top > spaceBelow) {
        popover.classList.add('archive-persona-portrait__popover--up');
      }
    }

    // Popover state. Open = pinned || hoveredTrigger || hoveredPopover. Hover only counts on
    // fine-pointer hover devices; a click pins it (a second click, an outside click, Escape or the
    // × close it). Touch keeps the plain tap toggle.
    //
    // suppressHover: set when the × closes the popover. The pointer is typically still where the
    // popover was, so hover is ignored until it has moved off the info control; hovering the icon
    // afterwards works normally again.
    const info = {
      pinned: false,
      hoveredTrigger: false,
      hoveredPopover: false,
      suppressHover: false,
      closeTimer: null
    };
    const HOVER_CLOSE_DELAY = 140;
    const hoverCapable = window.matchMedia('(hover: hover) and (pointer: fine)');

    function isInfoOpen() {
      return refs.infoButton.getAttribute('aria-expanded') === 'true';
    }

    function cancelInfoClose() {
      clearTimeout(info.closeTimer);
      info.closeTimer = null;
    }

    function renderInfo() {
      const shouldOpen = info.pinned || info.hoveredTrigger || info.hoveredPopover;
      if (shouldOpen === isInfoOpen()) {
        refs.info.classList.toggle('is-pinned', info.pinned);
        return;
      }

      refs.info.classList.toggle('is-pinned', info.pinned);
      refs.infoButton.setAttribute('aria-expanded', String(shouldOpen));

      if (shouldOpen) {
        refs.popover.hidden = false;
        positionPopover();
        requestAnimationFrame(() => {
          if (isInfoOpen()) {
            refs.info.classList.add('is-open');
          }
        });
      } else {
        refs.info.classList.remove('is-open');
        refs.popover.hidden = true;
      }
    }

    function pinInfo() {
      cancelInfoClose();
      info.pinned = true;
      renderInfo();
    }

    function closeInfo(returnFocus = false) {
      cancelInfoClose();
      const wasOpen = isInfoOpen();
      info.pinned = false;
      info.hoveredTrigger = false;
      info.hoveredPopover = false;
      renderInfo();

      if (returnFocus && wasOpen) {
        refs.infoButton.focus();
      }
    }

    function setInfoHover(part, hovered) {
      if (hovered) {
        cancelInfoClose();
        info[part] = true;
        renderInfo();
        return;
      }

      info[part] = false;
      if (info.pinned || info.hoveredTrigger || info.hoveredPopover) {
        return;
      }

      // Brief delay so crossing the gap between icon and popover does not flicker.
      cancelInfoClose();
      info.closeTimer = setTimeout(() => {
        info.closeTimer = null;
        renderInfo();
      }, HOVER_CLOSE_DELAY);
    }

    function bindInfoHover(element, part) {
      element.addEventListener('pointerenter', event => {
        if (event.pointerType === 'mouse' && hoverCapable.matches && !info.suppressHover) {
          setInfoHover(part, true);
        }
      });
      element.addEventListener('pointerleave', event => {
        if (event.pointerType !== 'mouse') {
          return;
        }
        if (part === 'hoveredTrigger') {
          info.suppressHover = false;
        }
        if (info[part]) {
          setInfoHover(part, false);
        }
      });
    }

    // × close: closes immediately whatever opened it (pin or hover) and holds off hover until the
    // pointer moves away, so the still-present pointer cannot reopen it.
    function closeInfoFromButton(event) {
      info.suppressHover = true;
      closeInfo(event.detail === 0);
    }

    function renderPopoverCopy() {
      const list = refs.popover.querySelector('[data-archive-portrait-requirements]');
      list.replaceChildren(
        ...requirementItems().map(text => {
          const item = document.createElement('li');
          item.textContent = text;
          return item;
        })
      );
    }

    // ---------------------------------------------------------------- upload / remove

    async function uploadPhoto(file) {
      state.busy = true;
      syncActions();
      announce('Updating your profile photo…');

      try {
        const processed = await processImageFile(file);

        const target = await requestJson('/profile/image/upload-url', {
          method: 'POST',
          body: JSON.stringify({ contentType: processed.contentType })
        });

        const upload = await fetch(target.uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': processed.contentType },
          body: processed.blob
        });

        if (!upload.ok) {
          throw photoError('upload_failed');
        }

        const result = await requestJson('/profile/image', {
          method: 'POST',
          body: JSON.stringify({ uploadToken: target.uploadToken })
        });

        setImage(result.profileImageUrl);
        announce('Profile photo updated.');
        toast({
          type: 'success',
          title: 'Profile Photo Updated',
          message: 'Your new photo now represents your Archive Profile.'
        });
      } catch (error) {
        announce('');
        toast({ type: 'error', title: 'Photo Not Updated', message: errorMessage(error) });
      } finally {
        state.busy = false;
        refs.input.value = '';
        syncActions();
      }
    }

    function showConfirm(show) {
      refs.confirm.hidden = !show;
      refs.actions.classList.toggle('archive-persona-portrait__actions--confirming', show);
      syncActions();
      if (show) {
        closeInfo();
        refs.confirmCancel.focus();
      }
    }

    async function removePhoto() {
      state.busy = true;
      syncActions();
      refs.confirmYes.disabled = true;

      try {
        await requestJson('/profile/image/remove', { method: 'POST' });
        showConfirm(false);
        setImage(null);
        announce('Profile photo removed.');
        toast({
          type: 'success',
          title: 'Profile Photo Removed',
          message: 'Your Archive Profile now shows the default portrait.'
        });
        refs.choose.focus();
      } catch (error) {
        showConfirm(false);
        toast({ type: 'error', title: 'Photo Not Removed', message: errorMessage(error) });
      } finally {
        state.busy = false;
        refs.confirmYes.disabled = false;
        syncActions();
      }
    }

    if (refs) {
      renderPopoverCopy();
      refs.input.setAttribute('accept', ARCHIVE_PROFILE_PHOTO_RULES.acceptedTypes.join(','));

      refs.choose.addEventListener('click', () => {
        if (!state.busy) {
          refs.input.click();
        }
      });

      // The portrait is a pointer shortcut for ADD / CHANGE PHOTO. It stays out of the tab order:
      // keyboard and screen-reader users reach the same action through the labelled text button.
      frame.addEventListener('click', () => {
        if (state.ready && !state.busy && refs.confirm.hidden) {
          refs.input.click();
        }
      });

      refs.input.addEventListener('change', () => {
        const file = refs.input.files && refs.input.files[0];
        if (file) {
          uploadPhoto(file);
        }
      });

      refs.remove.addEventListener('click', () => showConfirm(true));
      refs.confirmCancel.addEventListener('click', () => {
        showConfirm(false);
        refs.remove.focus();
      });
      refs.confirmYes.addEventListener('click', removePhoto);

      // Click / tap / Enter / Space: pins a closed or hover-opened popover; a second click on
      // a pinned popover closes it (and ignores the still-hovering pointer until it re-enters).
      refs.infoButton.addEventListener('click', () => {
        if (info.pinned) {
          closeInfo();
        } else {
          pinInfo();
        }
      });

      bindInfoHover(refs.infoButton, 'hoveredTrigger');
      bindInfoHover(refs.popover, 'hoveredPopover');
      refs.popoverClose.addEventListener('click', closeInfoFromButton);

      document.addEventListener('pointermove', event => {
        if (info.suppressHover && !refs.info.contains(event.target)) {
          info.suppressHover = false;
        }
      });

      document.addEventListener('pointerdown', event => {
        if (isInfoOpen() && !refs.info.contains(event.target)) {
          closeInfo();
        }
      });

      document.addEventListener('keydown', event => {
        if (event.key !== 'Escape') {
          return;
        }

        if (isInfoOpen()) {
          closeInfo(true);
        } else if (!refs.confirm.hidden) {
          showConfirm(false);
          refs.remove.focus();
        }
      });

      window.addEventListener('resize', () => {
        if (isInfoOpen()) {
          positionPopover();
        }
      });
    }

    showImage(null);
    syncActions();

    return { element, setImage, isMember, state };
  }

  function init() {
    const element = document.querySelector('[data-archive-portrait]');
    if (element && !portrait) {
      portrait = create(element);
    }
    return portrait;
  }

  window.ArchivePersonaPortrait = {
    rules: ARCHIVE_PROFILE_PHOTO_RULES,
    requirementItems,
    computeCenterCrop,
    processImageFile,
    connect(options = {}) {
      if (options.apiBase) {
        connection.apiBase = options.apiBase;
      }
      if (typeof options.showToast === 'function') {
        connection.showToast = options.showToast;
      }
      return init();
    },
    // Called by archive-persona.js once the signed-in member's profile has loaded.
    setImage(url) {
      const current = init();
      if (current && current.isMember) {
        current.setImage(url);
      }
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
